import React, { useState, useEffect } from 'react';
import { Delete, Lock } from 'lucide-react';

const PIN_KEY = 'app_pin';
const PIN_ENABLED_KEY = 'app_pin_enabled';

export const PinSetup = ({ onSave, onCancel }) => {
  const [step, setStep] = useState('enter'); // enter | confirm
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');

  const current = step === 'enter' ? pin : confirmPin;
  const setCurrent = step === 'enter' ? setPin : setConfirmPin;

  const handleDigit = (d) => {
    if (current.length < 4) setCurrent(prev => prev + d);
  };

  const handleDelete = () => setCurrent(prev => prev.slice(0, -1));

  useEffect(() => {
    if (step === 'enter' && pin.length === 4) {
      setTimeout(() => setStep('confirm'), 200);
    }
    if (step === 'confirm' && confirmPin.length === 4) {
      if (confirmPin === pin) {
        localStorage.setItem(PIN_KEY, pin);
        localStorage.setItem(PIN_ENABLED_KEY, 'true');
        onSave();
      } else {
        setError('הPIN לא תואם - נסה שוב');
        setConfirmPin('');
        setTimeout(() => { setError(''); setStep('enter'); setPin(''); }, 1500);
      }
    }
  }, [pin, confirmPin, step]);

  return (
    <div className="fixed inset-0 bg-stone-900 flex flex-col items-center justify-center z-50">
      <Lock className="w-10 h-10 text-brand-400 mb-4" />
      <h2 className="text-white text-xl font-bold mb-2">
        {step === 'enter' ? 'הגדר PIN' : 'אשר PIN'}
      </h2>
      <p className="text-stone-400 text-sm mb-8">
        {step === 'enter' ? 'בחר 4 ספרות' : 'הכנס שוב לאישור'}
      </p>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {/* Dots */}
      <div className="flex gap-4 mb-10">
        {[0,1,2,3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
            current.length > i ? 'bg-brand-400 border-brand-400' : 'border-stone-500'
          }`} />
        ))}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-4">
        {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
          <button
            key={i}
            onClick={() => k === '⌫' ? handleDelete() : k !== '' ? handleDigit(String(k)) : null}
            className={`w-18 h-16 rounded-2xl text-2xl font-bold transition-all active:scale-95 ${
              k === '' ? 'invisible' :
              k === '⌫' ? 'bg-stone-700 text-stone-300' :
              'bg-stone-700 text-white hover:bg-stone-600'
            }`}
            style={{ width: '72px' }}
          >
            {k}
          </button>
        ))}
      </div>

      <button onClick={onCancel} className="mt-8 text-stone-500 text-sm">ביטול</button>
    </div>
  );
};

export const PinUnlock = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  const handleDigit = (d) => {
    if (pin.length < 4) setPin(prev => prev + d);
  };

  useEffect(() => {
    if (pin.length === 4) {
      const savedPin = localStorage.getItem(PIN_KEY);
      if (pin === savedPin) {
        onUnlock();
      } else {
        setAttempts(a => a + 1);
        setError(`PIN שגוי (${attempts + 1}/5)`);
        setPin('');
        if (attempts >= 4) {
          setError('יותר מדי ניסיונות - נסה שוב מאוחר יותר');
        }
      }
    }
  }, [pin]);

  return (
    <div className="fixed inset-0 bg-stone-900 flex flex-col items-center justify-center z-50">
      <div className="text-5xl mb-4">💙</div>
      <h2 className="text-white text-xl font-bold mb-2">יומן גמילה</h2>
      <p className="text-stone-400 text-sm mb-8">הכנס PIN לפתיחה</p>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      <div className="flex gap-4 mb-10">
        {[0,1,2,3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
            pin.length > i ? 'bg-brand-400 border-brand-400' : 'border-stone-500'
          }`} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
          <button
            key={i}
            onClick={() => k === '⌫' ? setPin(p => p.slice(0,-1)) : k !== '' ? handleDigit(String(k)) : null}
            className={`w-18 h-16 rounded-2xl text-2xl font-bold transition-all active:scale-95 ${
              k === '' ? 'invisible' :
              k === '⌫' ? 'bg-stone-700 text-stone-300' :
              'bg-stone-700 text-white hover:bg-stone-600'
            }`}
            style={{ width: '72px' }}
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
};

export const usePinLock = () => {
  const isEnabled = localStorage.getItem(PIN_ENABLED_KEY) === 'true';

  const disable = () => {
    localStorage.removeItem(PIN_KEY);
    localStorage.removeItem(PIN_ENABLED_KEY);
  };

  return { isEnabled, disable };
};
